import React, { useState } from 'react'

import { format, formatDistanceToNow } from 'date-fns'
import Link from 'next/link'
import { GoTelescope } from 'react-icons/go'
import {
  HiMiniBolt,
  HiOutlineClock,
  HiOutlineSquare3Stack3D,
} from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import { UserAvatar } from '@/components/common/Avatar'
import Button from '@/components/common/Button'
import {
  OrgPermissionGate,
  ResourcePermissionGate,
} from '@/components/common/PermissionGate'
import { Skeleton } from '@/components/common/Skeleton'
import { useHomeDashboardOpenCreatePursuitDialog } from '@/components/home/dashboard/context'
import { PremiumAccessPopover } from '@/components/home/ui/PremiumAccessPopover'
import { Surface } from '@/components/home/ui/Surface'
import { DueThisWeek, isDueThisWeek } from '@/components/home/ui/Tag'
import { ColorIndicator } from '@/components/tags/ColorIndicator'

import { removeMarkdownFormatting } from '@/helpers/string'

import { useCanAccessPremium } from '@/hooks/billing/useCanAccessPremium'
import { useSafePush } from '@/hooks/navigation/useSafePush'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useOrganizationUsers } from '@/hooks/organization/useOrganizationUsers'
import { type RecentlyViewedPursuit } from '@/hooks/recently-viewed/types'
import {
  type RecentlyViewedPursuitsView,
  useRecentlyViewedPursuits,
} from '@/hooks/recently-viewed/useRecentlyViewedPursuits'

import { UUID } from '@/types/Id'

const MAX_RECENTLY_VIEWED_PURSUITS = 5

interface RecentlyViewedPursuitsProps {}

const RecentlyViewedPursuitsHeader: React.FC = () => {
  const { activeOrgId } = useActiveOrgId()

  const openCreatePursuitDialog = useHomeDashboardOpenCreatePursuitDialog()

  return (
    <>
      <Surface.Header>
        <Surface.Header.Icon icon={HiOutlineSquare3Stack3D} />
        <Surface.Header.Title>
          <span className="hidden md:block">Recently Viewed Pursuits</span>
          <span className="block md:hidden">Recently Viewed</span>
        </Surface.Header.Title>
        <Surface.Header.Menu className="flex items-center gap-x-3 -mt-[4px]">
          <Link
            href={activeOrgId !== '' ? `/orgs/${activeOrgId}/pursuits` : '#'}
            className="whitespace-nowrap text-xs font-medium text-primary-600 dark:text-primary-400 hover:opacity-70"
          >
            View All
          </Link>
          <OrgPermissionGate permission="write" fallback="hide">
            <Button
              variant="primary-compact"
              className="h-6 text-xs font-medium"
              onClick={openCreatePursuitDialog}
            >
              Add New
            </Button>
          </OrgPermissionGate>
        </Surface.Header.Menu>
      </Surface.Header>
    </>
  )
}

const RecentlyViewedPursuitsSkeleton: React.FC = () => {
  return (
    <Surface>
      <RecentlyViewedPursuitsHeader />
      <Surface.Content className="space-y-4">
        {Array.from({ length: MAX_RECENTLY_VIEWED_PURSUITS }).map(
          (_, index) => (
            <Skeleton className="h-20 w-full rounded-lg" key={index} />
          ),
        )}
      </Surface.Content>
    </Surface>
  )
}

const RecentlyViewedPursuitsCompactSkeleton: React.FC = () => {
  const { activeOrgId } = useActiveOrgId()
  return (
    <Surface>
      <Surface.Header>
        <Surface.Header.Icon icon={HiOutlineSquare3Stack3D} />
        <Surface.Header.Title>Recently Viewed Pursuits</Surface.Header.Title>
        <Surface.Header.Menu className="flex items-center gap-x-3">
          <Link
            href={activeOrgId !== '' ? `/orgs/${activeOrgId}/pursuits` : '#'}
            className="whitespace-nowrap text-xs font-medium text-primary-600 dark:text-primary-400 hover:opacity-70"
          >
            View All
          </Link>
        </Surface.Header.Menu>
      </Surface.Header>
      <Surface.Content className="space-y-4">
        {Array.from({ length: MAX_RECENTLY_VIEWED_PURSUITS }).map(
          (_, index) => (
            <Skeleton className="h-10 w-full rounded-lg" key={index} />
          ),
        )}
      </Surface.Content>
    </Surface>
  )
}

interface RecentlyViewedPursuitsSubheaderProps {
  activeTab: RecentlyViewedPursuitsView
  disabled?: boolean
  setActiveTab: (tab: RecentlyViewedPursuitsView) => void
}

const RecentlyViewedPursuitsSubheader: React.FC<
  RecentlyViewedPursuitsSubheaderProps
> = ({ activeTab, disabled, setActiveTab }) => {
  const handleChangeTab = (tab: RecentlyViewedPursuitsView) => () => {
    setActiveTab(tab)
  }
  return (
    <div className="flex gap-2">
      <Button
        variant={activeTab === 'all' ? 'primary-compact' : 'ghost-compact'}
        className={twMerge(
          'h-6 text-xs font-medium',
          disabled && 'opacity-50 cursor-not-allowed',
          activeTab === 'all' && 'hover:!opacity-100',
        )}
        onClick={handleChangeTab('all')}
      >
        All Pursuits
      </Button>
      <Button
        variant={activeTab === 'proposal' ? 'primary-compact' : 'ghost-compact'}
        className={twMerge(
          'h-6 text-xs font-medium',
          disabled && 'opacity-50 cursor-not-allowed',
          activeTab === 'proposal' && 'hover:!opacity-100',
        )}
        onClick={handleChangeTab('proposal')}
      >
        Proposal Stage
      </Button>
      <Button
        variant={activeTab === 'active' ? 'primary-compact' : 'ghost-compact'}
        className={twMerge(
          'h-6 text-xs font-medium',
          disabled && 'opacity-50 cursor-not-allowed',
          activeTab === 'proposal' && 'hover:!opacity-100',
        )}
        onClick={handleChangeTab('active')}
      >
        Active Only
      </Button>
    </div>
  )
}

interface RecentlyViewedPursuitProps {
  pursuit: RecentlyViewedPursuit
}

interface GoToProposalButtonProps {
  pursuit: RecentlyViewedPursuit
  children?: React.ReactNode
  open?: boolean
  triggerClassName?: string
  onOpenChange?: (open: boolean) => void
}

const GoToProposalButton: React.FC<GoToProposalButtonProps> = ({
  pursuit,
  children,
  open,
  onOpenChange,
  triggerClassName,
}) => {
  const { canAccessPremium } = useCanAccessPremium()
  const { safePush } = useSafePush()

  const handleLinkClick = () => {
    if (!canAccessPremium) {
      return
    }
    safePush(
      `/orgs/${pursuit.organization_id}/pursuits/${pursuit.id}/documents`,
    )
  }

  return (
    <PremiumAccessPopover
      open={open}
      onOpenChange={onOpenChange}
      triggerClassName={triggerClassName}
    >
      {children ? (
        children
      ) : (
        <Button
          variant="outline-compact"
          className="h-6 text-xs font-medium dark:hover:bg-primary-700"
          onClick={handleLinkClick}
        >
          <HiMiniBolt
            size={13}
            className="text-accent-500 dark:text-accent-400"
          />
          {pursuit.has_proposal_content ? 'Resume Proposal' : 'Start Proposal'}
        </Button>
      )}
    </PremiumAccessPopover>
  )
}

const RecentlyViewedPursuit: React.FC<RecentlyViewedPursuitProps> = ({
  pursuit,
}) => {
  const dueDate = new Date(pursuit.due_date)
  return (
    <div className="bg-primary-50 dark:bg-primary-900 divide-y divide-primary-200 dark:divide-primary-800 rounded-md p-3 hover:bg-primary-100 dark:hover:bg-primary-800/50 cursor-pointer transition-colors border border-primary-200 dark:border-primary-800">
      <div className="flex justify-between">
        <div className="flex items-start gap-2 w-full">
          <div className="flex flex-col items-start justify-between gap-2 w-full">
            <div className="flex flex-col md:flex-row items-start justify-between w-full gap-3">
              <Link
                href={`/orgs/${pursuit.organization_id}/pursuits/${pursuit.id}`}
                className="flex items-start justify-between w-full"
              >
                <div className="flex flex-col items-start gap-2 w-full">
                  <h3 className="font-medium text-primary-800 dark:text-primary-100 line-clamp-2">
                    {pursuit.title}
                  </h3>
                </div>
              </Link>
              <div className="hidden md:flex items-center gap-2">
                <GoToProposalButton pursuit={pursuit} triggerClassName="h-6" />
                <ResourcePermissionGate
                  resourceType="pursuit"
                  resourceId={pursuit.id}
                  permission="write"
                  fallback="hide"
                >
                  <Button
                    variant="primary-compact"
                    className="h-6 text-xs font-medium"
                  >
                    <Link
                      href={`/orgs/${pursuit.organization_id}/pursuits/${pursuit.id}/chat`}
                    >
                      Ask AI
                    </Link>
                  </Button>
                </ResourcePermissionGate>
              </div>
            </div>
            <Link
              href={`/orgs/${pursuit.organization_id}/pursuits/${pursuit.id}`}
              className="flex flex-col w-full gap-y-2"
            >
              {pursuit.description && pursuit.description.length > 0 && (
                <div className="w-full">
                  <p className="text-[13px] text-primary-600 dark:text-primary-400 line-clamp-1">
                    {removeMarkdownFormatting(pursuit.description)}
                  </p>
                </div>
              )}
              <div className="flex flex-wrap items-center justify-between w-full gap-x-4 gap-y-2">
                <div className="flex flex-wrap items-center text-xs text-primary-700 dark:text-primary-200 gap-4">
                  <div className="flex items-center gap-x-2 whitespace-nowrap">
                    <ColorIndicator color={pursuit.stage.color} />
                    <span className="font-medium">{pursuit.stage.name}</span>
                  </div>
                  <span className="flex items-center whitespace-nowrap">
                    <HiOutlineClock
                      size={13}
                      className="mr-1 text-red-600 dark:text-red-500"
                    />
                    <span>
                      Due:{' '}
                      <span className="font-medium">
                        {format(new Date(pursuit.due_date), 'MMM d, yyyy')}
                      </span>
                    </span>
                  </span>
                  {isDueThisWeek(dueDate) && <DueThisWeek />}
                </div>
                <div className="hidden md:flex items-center gap-x-2">
                  {pursuit.user_ids.length > 0 && (
                    <div className="flex item-center -space-x-2">
                      {pursuit.user_ids.map((user_id) => (
                        <div
                          key={user_id}
                          className="flex items-center gap-x-2 ring-1 ring-primary-100 dark:ring-primary-800 rounded-full"
                        >
                          <UserAvatar
                            key={user_id}
                            userId={user_id}
                            width={22}
                            height={22}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

const NoRecentlyViewedPursuits: React.FC = () => {
  const openCreatePursuitDialog = useHomeDashboardOpenCreatePursuitDialog()

  return (
    <Surface.Placeholder className="py-8">
      <div className="flex flex-col items-center text-center gap-y-2">
        <span className="flex flex-col xl:flex-row items-center xl:items-start gap-2 text-md font-semibold text-primary-900 dark:text-primary-100">
          <GoTelescope
            size={16}
            className="hidden md:block flex-shrink-0 text-green-600 dark:text-green-500 mt-[2px]"
          />
          <span>No recently viewed pursuits.</span>
        </span>{' '}
        <OrgPermissionGate permission="write" fallback="hide">
          <span className="text-[13px] font-medium text-primary-500 dark:text-primary-400">
            <button
              className="text-blue-600 dark:text-blue-400 hover:opacity-70"
              onClick={openCreatePursuitDialog}
            >
              Click here to create a new pursuit.
            </button>
          </span>
        </OrgPermissionGate>
      </div>
    </Surface.Placeholder>
  )
}

export const RecentlyViewedPursuitCompact: React.FC<
  RecentlyViewedPursuitProps
> = ({ pursuit }) => {
  const [showProposalButton, setShowProposalButton] = useState<boolean>(false)
  const [premiumFeaturePopoverIsOpen, setPremiumFeaturePopoverIsOpen] =
    useState<boolean>(false)

  const { canAccessPremium } = useCanAccessPremium()
  const { safePush } = useSafePush()

  const handleMouseEnter = () => {
    setShowProposalButton(true)
  }

  const handleMouseLeave = () => {
    if (premiumFeaturePopoverIsOpen) {
      return
    }
    setShowProposalButton(false)
  }

  const handleOpenChange = (open: boolean) => {
    setPremiumFeaturePopoverIsOpen(open)
    if (!open) {
      setShowProposalButton(false)
    }
  }

  const handleLinkClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!canAccessPremium) {
      return
    }
    safePush(
      `/orgs/${pursuit.organization_id}/pursuits/${pursuit.id}/documents`,
    )
  }

  return (
    <div
      className="flex flex-col"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex items-start justify-between gap-3 leading-4">
        <Link
          href={`/orgs/${pursuit.organization_id}/pursuits/${pursuit.id}`}
          className="truncate text-sm font-medium text-primary-900 dark:text-primary-100 hover:opacity-70"
        >
          <span>{pursuit.title}</span>
        </Link>
        {showProposalButton && (
          <GoToProposalButton
            pursuit={pursuit}
            open={premiumFeaturePopoverIsOpen}
            onOpenChange={handleOpenChange}
          >
            <button
              className="flex items-center gap-x-[6px] mt-[2px] whitespace-nowrap text-[13px] font-medium text-blue-600 dark:text-blue-400 hover:opacity-70"
              onClick={handleLinkClick}
            >
              {pursuit.has_proposal_content
                ? 'Resume Proposal'
                : 'Start Proposal'}
            </button>
          </GoToProposalButton>
        )}
      </div>
      <div className="flex flex-wrap gap-2 items-center mt-1 text-xs text-primary-500 dark:text-primary-400">
        <div className="flex items-center gap-[6px]">
          <ColorIndicator
            color={pursuit.stage.color}
            className="w-[9px] h-[9px]"
          />
          <span>{pursuit.stage.name}</span>
        </div>
        <span>•</span>
        <div className="flex items-center gap-x-1">
          <HiOutlineClock size={14} className="flex-shrink-0" />
          <span>
            Due{' '}
            {formatDistanceToNow(new Date(pursuit.due_date), {
              addSuffix: true,
            })}
          </span>
        </div>
      </div>
    </div>
  )
}

export const RecentlyViewedPursuitsCompact: React.FC<
  RecentlyViewedPursuitsProps
> = () => {
  const { activeOrgId } = useActiveOrgId()
  const { data: pursuits, isLoading } = useRecentlyViewedPursuits({
    orgId: activeOrgId,
  })

  if (!pursuits || isLoading) {
    return <RecentlyViewedPursuitsCompactSkeleton />
  }

  return (
    <Surface>
      <Surface.Header>
        <Surface.Header.Icon icon={HiOutlineSquare3Stack3D} />
        <Surface.Header.Title>Recently Viewed Pursuits</Surface.Header.Title>
        <Surface.Header.Menu className="flex items-center gap-x-3">
          <Link
            href={activeOrgId !== '' ? `/orgs/${activeOrgId}/pursuits` : '#'}
            className="whitespace-nowrap text-xs font-medium text-primary-600 dark:text-primary-400 hover:opacity-70"
          >
            View All
          </Link>
        </Surface.Header.Menu>
      </Surface.Header>
      <Surface.Content>
        <div className="space-y-4">
          {pursuits.length > 0 ? (
            pursuits.slice(0, MAX_RECENTLY_VIEWED_PURSUITS).map((pursuit) => {
              return (
                <RecentlyViewedPursuitCompact
                  key={pursuit.id}
                  pursuit={pursuit}
                />
              )
            })
          ) : (
            <NoRecentlyViewedPursuits />
          )}
        </div>
      </Surface.Content>
    </Surface>
  )
}

RecentlyViewedPursuitsCompact.displayName = 'Home.RecentlyViewedPursuitsCompact'

export const RecentlyViewedPursuits: React.FC<
  RecentlyViewedPursuitsProps
> = () => {
  const [activeTab, setActiveTab] = useState<RecentlyViewedPursuitsView>('all')

  const { activeOrgId } = useActiveOrgId()
  const { data: pursuits, isLoading } = useRecentlyViewedPursuits({
    orgId: activeOrgId,
    view: activeTab,
  })

  if (!pursuits || isLoading) {
    return <RecentlyViewedPursuitsSkeleton />
  }

  return (
    <Surface>
      <RecentlyViewedPursuitsHeader />
      <Surface.Content className="flex flex-col gap-4">
        <RecentlyViewedPursuitsSubheader
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
        {pursuits.length > 0 ? (
          pursuits
            .slice(0, MAX_RECENTLY_VIEWED_PURSUITS)
            .map((pursuit, index) => (
              <RecentlyViewedPursuit key={index} pursuit={pursuit} />
            ))
        ) : (
          <NoRecentlyViewedPursuits />
        )}
      </Surface.Content>
    </Surface>
  )
}

RecentlyViewedPursuits.displayName = 'Home.RecentlyViewedPursuits'
